<script setup>
// Public content page for the arrears search intent ("VvE betalingsachterstand",
// "VvE bijdrage niet betaald"). Body copy is authored in Dutch here rather than in
// i18n for the same reason the legal pages do it — see Privacybeleid.vue.
//
// Written for a penningmeester who has the problem right now, not for a buyer: the
// monthly checks, the collection policy and the sequence are useful whether or not
// the reader ever uses Pandig, and Pandig itself only appears near the end. A page
// that sells first would be worth less to the reader and to search.
//
// The Wki section is a boundary, not a feature. Pandig handles the agreed recurring
// payment and its status; out-of-court collection for third parties needs a Justis
// registration since 1 April 2025, and that part belongs to the VvE or a registered
// partner. Don't soften it, and keep the "geen juridisch advies" line on the page.
import MarketingShell from "@/components/marketing/MarketingShell.vue";

const link = "font-semibold text-primary underline-offset-4 hover:underline";

const monthlyChecks = [
  "Is de incasso gedraaid, en komt het binnengekomen bedrag overeen met het verwachte?",
  "Staat elke eenheid klaar: een eigenaar gekoppeld, een actieve incasso, een geldige machtiging?",
  "Zijn er betalingen mislukt, en sinds wanneer?",
  "Gaat het bij dezelfde eigenaar voor de tweede keer mis? Dat is een ander signaal dan één keer een te laag saldo.",
];

const policy = [
  {
    term: "Wanneer",
    body: "Na hoeveel dagen gaat de eerste herinnering eruit, en hoeveel tijd zit tussen de vervolgstappen?",
  },
  {
    term: "Wie",
    body: "Wie neemt contact op met de eigenaar: de penningmeester, een vast bestuurslid of de VvE-beheerder?",
  },
  {
    term: "Wat",
    body: "Wat staat er in een vriendelijke eerste herinnering, en wat in de laatste aanmaning?",
  },
  {
    term: "Tot hoever",
    body: "Na hoeveel termijnen gaat een dossier naar de formele route, en wie besluit dat?",
  },
  {
    term: "Betalingsregeling",
    body: "Onder welke voorwaarden mag het bestuur een regeling afspreken, en hoe leg je die vast?",
  },
  {
    term: "Kosten en rente",
    body: "Brengt de VvE buitengerechtelijke kosten of wettelijke rente in rekening, en waarop is dat gebaseerd?",
  },
];

const steps = [
  "Controleer eerst de machtiging. Ontbreekt die, is ze verlopen of wordt ze nog geverifieerd, dan is er geen betaalprobleem maar een inrichtingsprobleem.",
  "Kijk waarop de incasso is afgeketst. Een ontoereikend saldo, een opgeheven rekening en een teruggeboekte incasso zijn drie verschillende gesprekken.",
  "Weet dat de eigenaar al bericht heeft: bij een mislukte of teruggeboekte afschrijving mailt Pandig de melding zelf, met bedrag, eenheid en datum. Jouw eerste contact hoeft dus geen mededeling te zijn, maar een vraag.",
  "Weet dat de automatische tweede poging misschien al is geweest: Pandig biedt een mislukte afschrijving na een paar werkdagen één keer opnieuw aan. Staat de bijdrage daarna nog open, dan is dat het moment voor een gesprek.",
  "Spreek één oplossing af: opnieuw aanbieden op een afgesproken datum — met één klik bij Opvolgen, zolang die ene extra poging nog niet is gebruikt — zelf overmaken vóór een datum, of een regeling binnen het beleid.",
  "Leg de afspraak vast, met datum en met wie je sprak. Dat is later je dossier.",
  "Loopt de afspraak niet, volg dan het vastgestelde beleid: een schriftelijke aanmaning met een laatste termijn, en daarna de formele route.",
];

const doesDo = [
  "Incasseert de vastgestelde VvE-bijdrage per eenheid volgens het afgesproken ritme — maandelijks, per kwartaal, per half jaar of per jaar — op een geldige SEPA-machtiging.",
  "Laat vóór de incasso zien wat verwacht wordt, wanneer de volgende run is en welke eenheden nog niet kunnen innen.",
  "Zet bij Opvolgen alleen de uitzonderingen: een mislukte afschrijving of een terugboeking, met eenheid en eigenaar, en per betaling een gebeurtenissenlog. De volledige betaalhistorie staat er één klik vandaan.",
  "Mailt de eigenaar zelf bij een mislukte of teruggeboekte afschrijving — één nette melding met bedrag, eenheid en wat er gebeurde, zodat het bestuur die boodschap niet hoeft te brengen.",
  "Biedt een mislukte afschrijving na een paar werkdagen automatisch nog één keer aan op de bestaande machtiging — of eerder, als het bestuur erop klikt. Eén extra poging per mislukking, nooit meer.",
  "Houdt een geslaagde incasso zes werkdagen op voorlopig binnen en zet die daarna op definitief.",
  "Laat de eigenaar zijn eigen bedrag, machtiging en betalingen zien.",
];

const doesNot = [
  "Geen reeks herhaalpogingen: het blijft bij die ene extra poging per mislukte afschrijving — nooit herhaald incasseren op dezelfde rekening. Een terugboeking wordt nooit automatisch opnieuw aangeboden.",
  "Geen herinneringsreeks, aanmaningen, brieven, rente of achterstandsaldo, en geen vooraankondiging van de incasso. Het blijft bij die ene melding.",
  "Geen sms of pushberichten, en geen bericht aan de eigenaar over een geslaagde betaling of een aflopende machtiging.",
  "Geen boekhouding, jaarrekening of MJOP, en geen formele incasso.",
];

const sources = [
  {
    href: "https://www.rijksoverheid.nl/vraag-en-antwoord/huis-kopen/wat-houdt-een-vereniging-van-eigenaars-vve-in",
    label: "Rijksoverheid — wat houdt een Vereniging van Eigenaars (VvE) in?",
  },
  {
    href: "https://www.rijksoverheid.nl/vraag-en-antwoord/huis-kopen/vve-geld-reserveren-groot-onderhoud",
    label: "Rijksoverheid — geld reserveren voor groot onderhoud",
  },
  {
    href: "https://www.eigenhuis.nl/vve/bestuur-vve/financiering-vve/betalingsachterstanden-binnen-de-vve",
    label: "Vereniging Eigen Huis — betalingsachterstanden binnen de VvE",
  },
  {
    href: "https://www.eigenhuis.nl/vve/bestuur-vve",
    label: "Vereniging Eigen Huis — taken en plichten van het VvE-bestuur",
  },
  {
    href: "https://www.vve.nl/kennisbank/vve-financien/twee-manieren-om-maandelijkse-vve-bijdragen-te-innen-wat-past-bij-uw-vve",
    label: "VvE.nl — twee manieren om maandelijkse VvE-bijdragen te innen",
  },
  {
    href: "https://www.justis.nl/producten/screening-incassodienstverlening",
    label: "Justis — screening incassodienstverlening, waar de Wki over gaat",
  },
  {
    href: "https://www.justis.nl/nieuws/31-03-2025-per-1-april-2025-registratieplicht-incassodienstverleners-van-kracht",
    label: "Justis — registratieplicht incassodienstverleners per 1 april 2025",
  },
];
</script>

<template>
  <MarketingShell
    eyebrow="Voor penningmeester en bestuur"
    title="Betalingsachterstand in de VvE: vroeg zien, netjes oplossen"
    lead="Een achterstand in de VvE begint stil: één bijdrage die niet binnenkomt. Hieronder staat wat je maandelijks kunt controleren, wat je in een collectief betaalbeleid vastlegt, wat je doet bij een mislukte incasso, en waar de grens ligt tussen zelf innen en formele incasso."
  >
    <section>
      <h2 class="font-sans text-xl font-bold tracking-tight text-foreground">Een achterstand raakt meer dan de administratie</h2>
      <p class="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
        De VvE-bijdrage is geen abonnement, maar het werkkapitaal van de vereniging. Daaruit betaalt de VvE de verzekering, het schoonmaken, klein onderhoud en reparaties — en de reservering voor toekomstig groot onderhoud.
      </p>
      <p class="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
        Die reservering is niet vrijblijvend. Een VvE moet
        <a href="https://www.rijksoverheid.nl/vraag-en-antwoord/huis-kopen/vve-geld-reserveren-groot-onderhoud" target="_blank" rel="noopener noreferrer" :class="link">jaarlijks reserveren voor groot onderhoud</a>:
        op basis van een meerjarenonderhoudsplan (MJOP), met ten minste 0,5% van de herbouwwaarde als er geen MJOP is — of op een andere manier dan een reservefonds, als de vergadering daarvoor kiest. Ongeveer de helft van de VvE's spaart te weinig, schrijft de Rijksoverheid. Loopt de inning ook achter, dan is het geen administratieve rommel maar een gat in de vastgestelde begroting.
      </p>
      <p class="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
        Er komt iets bij dat in geen begroting staat: het gaat om je buur. Dat maakt uitstellen makkelijk. Juist daarom helpt het als het beleid bepaalt wat er gebeurt, en niet jij.
      </p>
    </section>

    <section>
      <h2 class="font-sans text-xl font-bold tracking-tight text-foreground">Het vroeg zien</h2>
      <p class="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
        Eén maand achterstand is een gesprek, zes maanden een dossier. Het verschil zit in hoe snel je het ziet. Wat je maandelijks kunt nalopen:
      </p>
      <ul class="mt-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
        <li v-for="check in monthlyChecks" :key="check">{{ check }}</li>
      </ul>
      <p class="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
        Een bankafschrift is geen betaaloverzicht: bankregels vertellen niet welke eenheid klaarstond, welk bedrag verwacht werd of waarom er niets binnenkwam.
      </p>
      <!-- A definition list, so the two silent failure modes can be scanned rather
           than read: they're why arrears go unnoticed for months. -->
      <dl class="mt-4 space-y-4 border-l-2 border-foreground/15 pl-4">
        <div>
          <dt class="font-sans text-sm font-semibold text-foreground">Een ontbrekende machtiging</dt>
          <dd class="mt-1 text-sm leading-relaxed text-muted-foreground sm:text-base">
            Een eenheid met een actieve incasso maar zonder geldige machtiging levert geen mislukte betaling op: er wordt simpelweg niets geïncasseerd — de stilste vorm van achterstand.
          </dd>
        </div>
        <div>
          <dt class="font-sans text-sm font-semibold text-foreground">Binnen is nog niet definitief</dt>
          <dd class="mt-1 text-sm leading-relaxed text-muted-foreground sm:text-base">
            Een SEPA-incasso die als geslaagd binnenkwam, kan later nog worden teruggeboekt (een storno). Reken een incasso pas na een aantal werkdagen als definitief; in Pandig staat een geslaagde incasso zes werkdagen als voorlopig binnen. Komt de terugboeking, dan verschijnt die bij Opvolgen en krijgt de eigenaar er bericht over.
          </dd>
        </div>
      </dl>
    </section>

    <section>
      <h2 class="font-sans text-xl font-bold tracking-tight text-foreground">Een collectief betaalbeleid, vastgesteld door de vergadering</h2>
      <p class="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
        Het VvE-bestuur is
        <a href="https://www.eigenhuis.nl/vve/bestuur-vve" target="_blank" rel="noopener noreferrer" :class="link">verantwoordelijk voor het innen van de bijdragen en de administratie</a>,
        ook wanneer het beheer is uitbesteed. Dat gaat lichter met beleid dat de vergadering heeft vastgesteld: dan hoef je per geval niets te bedenken. Leg vast:
      </p>
      <dl class="mt-4 space-y-4 border-l-2 border-foreground/15 pl-4">
        <div v-for="item in policy" :key="item.term">
          <dt class="font-sans text-sm font-semibold text-foreground">{{ item.term }}</dt>
          <dd class="mt-1 text-sm leading-relaxed text-muted-foreground sm:text-base">{{ item.body }}</dd>
        </div>
      </dl>
      <p class="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
        Eén beleid voor alle eigenaren voorkomt bovendien willekeur: gelijke gevallen, gelijke stappen.
      </p>
    </section>

    <section>
      <h2 class="font-sans text-xl font-bold tracking-tight text-foreground">Een mislukte incasso of een terugboeking: wat nu?</h2>
      <ol class="mt-3 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
        <li v-for="step in steps" :key="step">{{ step }}</li>
      </ol>
      <p class="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
        Twee dingen maken dit makkelijker: snel, en schriftelijk. Een eigenaar die na een week wordt gebeld ervaart service; een aanmaning na vier maanden voelt als een conflict.
      </p>
    </section>

    <section>
      <h2 class="font-sans text-xl font-bold tracking-tight text-foreground">Waar de grens ligt: de Wki</h2>
      <p class="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
        De Wet kwaliteit incassodienstverlening (Wki) stelt eisen aan bedrijven die buitengerechtelijke incassowerkzaamheden verrichten voor derden, op vorderingen op natuurlijke personen in Nederland. Sinds
        <a href="https://www.justis.nl/nieuws/31-03-2025-per-1-april-2025-registratieplicht-incassodienstverleners-van-kracht" target="_blank" rel="noopener noreferrer" :class="link">1 april 2025</a>
        geldt een registratieplicht: wie onder de wet valt, moet ingeschreven staan in het register van
        <a href="https://www.justis.nl/producten/screening-incassodienstverlening" target="_blank" rel="noopener noreferrer" :class="link">Justis</a>
        en aan kwaliteitseisen voldoen.
      </p>
      <p class="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
        Voor een VvE is het onderscheid dit: je eigen vordering op je eigen leden innen is iets anders dan incasso als dienst voor een ander. Besteed je die stap uit, controleer dan of die partij geregistreerd is.
      </p>
      <p class="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
        De grens van Pandig, expliciet: Pandig verzorgt de afgesproken terugkerende betaling en de status daarvan, maakt een mislukte betaling zichtbaar en stuurt daarover één zakelijke melding aan de eigenaar. Een aanmaningstraject hoort daar niet bij: het bestuur of de VvE-beheerder besluit over verdere invordering volgens het eigen beleid. Gereguleerde incasso doet de VvE zelf waar dat mag, of een geregistreerde partner. Pandig is geen incassodienstverlener.
      </p>
    </section>

    <section>
      <h2 class="font-sans text-xl font-bold tracking-tight text-foreground">Wat Pandig hierin doet, en niet doet</h2>
      <h3 class="mt-4 font-mono text-[0.6875rem] font-semibold uppercase tracking-[0.12em] text-primary">Wel</h3>
      <ul class="mt-2 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
        <li v-for="item in doesDo" :key="item">{{ item }}</li>
      </ul>
      <h3 class="mt-5 font-mono text-[0.6875rem] font-semibold uppercase tracking-[0.12em] text-primary">Niet</h3>
      <ul class="mt-2 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
        <li v-for="item in doesNot" :key="item">{{ item }}</li>
      </ul>
      <p class="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
        Beheer je meerdere VvE's, dan staat op
        <RouterLink to="/voor-vve-beheerders" :class="link">Pandig voor VvE-beheerders</RouterLink>
        hoe dit per VvE gescheiden blijft.
      </p>
    </section>

    <section>
      <h2 class="font-sans text-xl font-bold tracking-tight text-foreground">Bronnen</h2>
      <ul class="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
        <li v-for="source in sources" :key="source.href">
          <a :href="source.href" target="_blank" rel="noopener noreferrer" :class="link">{{ source.label }}</a>
        </li>
      </ul>
      <!-- Sources, not endorsements: none of these organisations has anything to do
           with Pandig, and the page shouldn't suggest otherwise. -->
      <p class="mt-5 border-t-2 border-foreground/10 pt-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
        Deze partijen zijn bronnen, geen partners van Pandig. Dit is algemene informatie, geen juridisch advies. Laat je bij een concreet dossier adviseren door iemand die jullie situatie kent.
      </p>
    </section>
  </MarketingShell>
</template>
